import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Empty, Pagination, Select, Space } from 'antd';
import { CheckCircleOutlined, ReloadOutlined, ScanOutlined } from '@ant-design/icons';
import {
  getDuplicateProductGroups,
  resolveAllRecommendedDuplicateGroups,
  resolveDuplicateProductGroup,
  scanDuplicateProducts,
} from '../../api/productDuplicates';
import type { Product } from '../../types/product';
import type { ProductDuplicateGroup } from '../../types/productDuplicate';
import ProductDetailDrawer from '../products/components/ProductDetailDrawer';
import DuplicateGroupCard from './DuplicateGroupCard';
import { hasRangeDifferentShippingScope } from './duplicateProductUtils';
import './index.css';

const DuplicateProductsPage: React.FC = () => {
  const { message, modal } = App.useApp();
  const [groups, setGroups] = useState<ProductDuplicateGroup[]>([]);
  const [status, setStatus] = useState('candidate');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [batchResolving, setBatchResolving] = useState(false);
  const [safeAllResolving, setSafeAllResolving] = useState(false);
  const [fullAllResolving, setFullAllResolving] = useState(false);
  const [resolvingKey, setResolvingKey] = useState<string | null>(null);
  const [detailProduct, setDetailProduct] = useState<Product | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getDuplicateProductGroups(status, page, pageSize);
      setGroups(data.items || []);
      setTotal(data.total || 0);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取重复卡候选失败');
    } finally {
      setLoading(false);
    }
  }, [message, page, pageSize, status]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleScan = async () => {
    try {
      setScanLoading(true);
      const result = await scanDuplicateProducts();
      message.success(
        `扫描完成：新增 ${result.candidateGroups} 组，更新 ${result.updatedGroups} 组，清理 ${result.removedGroups || 0} 组`,
      );
      if (page === 1) {
        await fetchGroups();
      } else {
        setPage(1);
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || '扫描重复卡失败');
    } finally {
      setScanLoading(false);
    }
  };

  const getRecommendedResolvableGroups = useCallback(() => groups.filter((group) => (
    group.status === 'candidate' &&
    !hasRangeDifferentShippingScope(group.different_fields) &&
    !!group.recommended_keep_product_id &&
    group.products.some((product) => product.id === group.recommended_keep_product_id)
  )), [groups]);

  const resolveGroup = async (group: ProductDuplicateGroup, keepProductId: number) => {
    const hideProductIds = group.products
      .map((product) => product.id)
      .filter((id) => id !== keepProductId);

    await resolveDuplicateProductGroup(group.group_key, {
      action: 'resolve',
      keepProductId,
      hideProductIds,
    });
  };

  const handleResolve = async (
    group: ProductDuplicateGroup,
    keepProductId: number,
  ) => {
    try {
      setResolvingKey(group.group_key);
      await resolveGroup(group, keepProductId);
      message.success('已确认隐藏重复产品');
      await fetchGroups();
    } catch (error: any) {
      message.error(error?.response?.data?.message || '处理重复卡失败');
    } finally {
      setResolvingKey(null);
    }
  };

  const handleResolveRecommended = async (group: ProductDuplicateGroup) => {
    const keepProductId = group.recommended_keep_product_id;
    if (!keepProductId || !group.products.some((product) => product.id === keepProductId)) {
      message.warning('当前候选组没有可用的推荐套餐');
      return;
    }
    if (hasRangeDifferentShippingScope(group.different_fields)) {
      message.warning('该候选地区范围不同，需要人工选择保留套餐');
      return;
    }

    await handleResolve(group, keepProductId);
  };

  const handleResolvePageRecommendations = () => {
    const targetGroups = getRecommendedResolvableGroups();
    if (targetGroups.length === 0) {
      message.warning('当前页没有可处理的推荐候选组');
      return;
    }

    modal.confirm({
      title: '保留本页推荐套餐',
      content: `将处理当前页 ${targetGroups.length} 组候选：保留系统推荐套餐，并隐藏同组其他套餐。`,
      okText: '确认处理',
      cancelText: '取消',
      onOk: async () => {
        let successCount = 0;
        let failedCount = 0;

        try {
          setBatchResolving(true);
          for (const group of targetGroups) {
            try {
              await resolveGroup(group, group.recommended_keep_product_id as number);
              successCount++;
            } catch (error) {
              failedCount++;
            }
          }

          if (failedCount > 0) {
            message.warning(`已处理 ${successCount} 组，失败 ${failedCount} 组`);
          } else {
            message.success(`已处理当前页 ${successCount} 组推荐套餐`);
          }
          await fetchGroups();
        } finally {
          setBatchResolving(false);
        }
      },
    });
  };

  const handleResolveAllRecommendations = (includeRangeDifferent: boolean) => {
    modal.confirm({
      title: includeRangeDifferent ? '保留全部推荐套餐' : '保留当前规则推荐套餐',
      content: includeRangeDifferent
        ? '将处理全部待处理候选组，包括“只发广东全省 vs 只发广东个别城市”这类地区范围不同的候选。系统会保留推荐套餐并隐藏同组其他套餐，请确认你愿意按推荐结果批量处理。'
        : '将只处理当前保守规则命中的候选：地区范围完全一致的推荐套餐会自动处理，地区范围不同的候选会跳过并保留给人工确认。',
      okText: includeRangeDifferent ? '确认处理全部' : '确认处理当前规则',
      cancelText: '取消',
      onOk: async () => {
        try {
          if (includeRangeDifferent) {
            setFullAllResolving(true);
          } else {
            setSafeAllResolving(true);
          }
          const result = await resolveAllRecommendedDuplicateGroups({ includeRangeDifferent });
          const skippedRangeDifferent = result.skippedRangeDifferent || 0;
          const skipped = result.skippedNoRecommendation + result.skippedOverlap + result.skippedInvalid + skippedRangeDifferent;
          const reasonText = skippedRangeDifferent > 0 ? `，其中 ${skippedRangeDifferent} 组地区范围不同需人工确认` : '';
          if (result.failed > 0 || skipped > 0) {
            message.warning(
              `已处理 ${result.succeeded} 组（${result.resolvedComponents || 0} 个重复簇），失败 ${result.failed} 组，跳过 ${skipped} 组${reasonText}`,
            );
          } else {
            message.success(`已处理全部推荐 ${result.succeeded} 组（${result.resolvedComponents || 0} 个重复簇）`);
          }
          if (page === 1) {
            await fetchGroups();
          } else {
            setPage(1);
          }
        } catch (error: any) {
          message.error(error?.response?.data?.message || '处理全部推荐失败');
        } finally {
          setSafeAllResolving(false);
          setFullAllResolving(false);
        }
      },
    });
  };

  const handleGroupAction = async (
    group: ProductDuplicateGroup,
    action: 'ignore' | 'restore',
  ) => {
    try {
      setResolvingKey(group.group_key);
      await resolveDuplicateProductGroup(group.group_key, { action });
      message.success(action === 'ignore' ? '已忽略该候选组' : '已恢复展示');
      await fetchGroups();
    } catch (error: any) {
      message.error(error?.response?.data?.message || '操作失败');
    } finally {
      setResolvingKey(null);
    }
  };

  const recommendedResolvableCount = getRecommendedResolvableGroups().length;

  return (
    <div className="duplicate-products-page">
      <div className="duplicate-products-toolbar">
        <Space wrap>
          <Select
            value={status}
            style={{ width: 150 }}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            options={[
              { value: 'candidate', label: '待处理' },
              { value: 'confirmed', label: '已确认隐藏' },
              { value: 'ignored', label: '已忽略' },
              { value: 'all', label: '全部' },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchGroups} loading={loading}>
            刷新
          </Button>
        </Space>
        <Space wrap>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={handleResolvePageRecommendations}
            loading={batchResolving}
            disabled={loading || scanLoading || safeAllResolving || fullAllResolving || recommendedResolvableCount === 0}
          >
            保留本页推荐
          </Button>
          <Button
            icon={<CheckCircleOutlined />}
            onClick={() => handleResolveAllRecommendations(false)}
            loading={safeAllResolving}
            disabled={loading || scanLoading || batchResolving || fullAllResolving || total === 0}
          >
            保留当前规则推荐
          </Button>
          <Button
            danger
            icon={<CheckCircleOutlined />}
            onClick={() => handleResolveAllRecommendations(true)}
            loading={fullAllResolving}
            disabled={loading || scanLoading || batchResolving || safeAllResolving || total === 0}
          >
            保留全部推荐
          </Button>
          <Button type="primary" icon={<ScanOutlined />} onClick={handleScan} loading={scanLoading}>
            扫描重复卡
          </Button>
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="月租、通用流量、通话时长、权益一致且相似度 85% 以上才会进入候选；扫描只生成候选，不会自动隐藏产品。"
        description="“保留当前规则推荐”会跳过地区范围不同候选；“保留全部推荐”会包含这类候选，例如只发全省 vs 只发部分城市。"
      />

      {groups.length === 0 && !loading ? (
        <Empty className="duplicate-products-empty" description="暂无重复卡候选" />
      ) : (
        <div className="duplicate-products-list">
          {groups.map((group) => (
            <DuplicateGroupCard
              key={group.group_key}
              group={group}
              resolvingKey={resolvingKey}
              onShowProduct={(product) => {
                setDetailProduct(product);
                setDetailVisible(true);
              }}
              onResolveRecommended={handleResolveRecommended}
              onResolve={handleResolve}
              onGroupAction={handleGroupAction}
            />
          ))}
        </div>
      )}

      {total > 0 && (
        <div className="duplicate-products-pagination">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            showTotal={(value) => `共 ${value} 组`}
            onChange={(nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            }}
          />
        </div>
      )}

      <ProductDetailDrawer
        open={detailVisible}
        product={detailProduct}
        platform172Detail={null}
        zIndex={1200}
        onClose={() => {
          setDetailVisible(false);
          setDetailProduct(null);
        }}
      />
    </div>
  );
};

export default DuplicateProductsPage;
